import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Button, Card, Col, Empty, Row, Spin, Tag, Typography } from 'antd';
import {
  CalendarOutlined, CloudOutlined, FolderOutlined, ProfileOutlined, UserOutlined,
} from '@ant-design/icons';
import api from '../api/client';

const { Title, Text } = Typography;

/**
 * «Мои доступы» — единственная страница гостевых пользователей (role_level=0).
 * Показывает выданные гранты: задачи, чаты, папки. Для сотрудников — их выданные
 * гостям права не здесь (админка гостей).
 */
export default function MyAccessPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/access/my')
      .then(r => setData(r.data))
      .catch(() => setData({ grants: [], is_guest: true }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;

  const grants = data?.grants || [];
  const tasks = grants.filter(g => g.resource_type === 'task');
  const chats = grants.filter(g => g.resource_type === 'chat');
  const folders = grants.filter(g => g.resource_type === 'folder');

  const expiresTag = (g) => {
    if (!g.expires_at) return <Tag color="green">бессрочно</Tag>;
    const d = new Date(g.expires_at);
    const left = d - new Date();
    if (left < 0) return <Tag color="red">истёк</Tag>;
    const days = Math.ceil(left / 86400000);
    return <Tag color={days <= 7 ? 'orange' : 'blue'}>до {d.toLocaleDateString('ru-RU')}</Tag>;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Мои доступы</h1>
        <p className="page-subtitle">
          {data?.is_guest
            ? 'Внешний аккаунт: доступ только к перечисленному ниже'
            : 'Ваши выданные доступы'}
        </p>
      </div>

      {grants.length === 0 ? (
        <Empty description="Доступов пока не выдано — обратитесь к администратору" />
      ) : (
        <Row gutter={[20, 20]}>
          {tasks.length > 0 && (
            <Col xs={24} lg={12}>
              <Card title={<><ProfileOutlined /> Задачи ({tasks.length})</>}>
                {tasks.map(g => (
                  <div key={g.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                  }} onClick={() => navigate(`/tasks?taskId=${g.resource_id}`)}>
                    <div>
                      <Text strong>{g.task?.title || `Задача #${g.resource_id}`}</Text>
                      <div>
                        {g.task?.due && <Text type="secondary" style={{ fontSize: 12 }}>
                          <CalendarOutlined /> до {new Date(g.task.due).toLocaleDateString('ru-RU')}
                        </Text>}
                      </div>
                    </div>
                    <div>
                      <Tag color={g.permission === 'write' ? 'purple' : 'default'}>
                        {g.permission === 'write' ? 'комментарии' : 'просмотр'}
                      </Tag>
                      {expiresTag(g)}
                    </div>
                  </div>
                ))}
              </Card>
            </Col>
          )}

          {chats.length > 0 && (
            <Col xs={24} lg={12}>
              <Card title={<><CloudOutlined /> Чаты ({chats.length})</>}>
                {chats.map(g => (
                  <div key={g.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 0', borderBottom: '1px solid #f1f5f9',
                  }}>
                    <Text>Комната обсуждения</Text>
                    <Button type="primary" size="small" onClick={() => navigate('/talk')}>
                      Открыть чат
                    </Button>
                  </div>
                ))}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Все ваши комнаты — в разделе «Чат»
                </Text>
              </Card>
            </Col>
          )}

          {folders.length > 0 && (
            <Col xs={24} lg={12}>
              <Card title={<><FolderOutlined /> Папки документов ({folders.length})</>}>
                {folders.map(g => (
                  <div key={g.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 0', borderBottom: '1px solid #f1f5f9',
                  }}>
                    <Text>{g.resource_id}</Text>
                    <Button
                      size="small"
                      href="https://opp.spzvg.ru/"
                      target="_blank"
                      icon={<FolderOutlined />}
                    >
                      Открыть в Nextcloud
                    </Button>
                  </div>
                ))}
              </Card>
            </Col>
          )}
        </Row>
      )}
    </div>
  );
}
